import { ReactNode, useMemo, useRef } from 'react';
import * as React from 'react';
import { DataTreeDragPreview } from 'shared/data_tree/DataTreeDragPreview';
import { DragDropContainer } from 'shared/data_tree/styled';
import {
    TreeMovableConfig,
    TreeNode,
    TreeNodeId,
    TreeNodePosition,
    UseTreeDragDropNodeInfo,
    UseTreeDragDropTargetNodeInfo,
} from 'shared/data_tree/types';
import { useTreeDragDrop } from 'shared/data_tree/useTreeDragDrop';
import { tokens } from 'styles/tokens';
import { shallow } from 'zustand/shallow';

const BlankIcon =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Opacity of the node being dragged (not the drag preview).
 */
const MoveNodeOpacity = 0.25;

/**
 * The percentage of the total height of a node that is considered its edge.
 * This zone is used when dragging a node over another node to determine
 * if the node is "before", "after", or a "child" of the drop target node.
 * Example: If a node were 100px in height, the top and bottom edges would
 * each be 25px in height.
 *
 *  Drag Target Node (100px)
 * +---------------+
 * | before (25px) |
 * +---------------+
 * |    child      |
 * +---------------+
 * | after  (25px) |
 * +---------------+
 */
const HoverEdgeZone = 0.25;

type PreviewPosition = Readonly<{ x: number; y: number }>;

type ValidCheck = PreviewPosition &
    Readonly<{
        isValid: boolean;
    }>;

const getTargetMoveType = (
    event: React.DragEvent<HTMLElement>
): TreeNodePosition | undefined => {
    const { pageY, target } = event;
    if (target instanceof HTMLElement) {
        const { top, height } = target.getBoundingClientRect();
        const offset = pageY - top;
        const edgeZone = height * HoverEdgeZone;

        if (offset < edgeZone) {
            return 'before';
        }
        if (offset > height - edgeZone) {
            return 'after';
        }
        return 'child';
    }
    return undefined;
};

type GetBorderColorsOptions = Readonly<{
    treeId: string;
    dragNode: UseTreeDragDropNodeInfo | undefined;
    targetNode: UseTreeDragDropTargetNodeInfo | undefined;
    nodeId: TreeNodeId;
    color: string;
}>;

const TransparentBorderColors = 'transparent transparent transparent transparent';

const getBorderColors = ({
    treeId,
    dragNode,
    targetNode,
    nodeId,
    color,
}: GetBorderColorsOptions): React.CSSProperties['borderColor'] => {
    if (
        !dragNode ||
        !targetNode ||
        dragNode.nodeId === nodeId ||
        targetNode.treeId !== treeId
    ) {
        return TransparentBorderColors;
    }

    if (nodeId === targetNode.nodeId && targetNode.moveType === 'before') {
        return `${color} transparent transparent transparent`;
    }

    if (nodeId === targetNode.nodeId && targetNode.moveType === 'after') {
        return `transparent transparent ${color} transparent`;
    }

    if (
        (nodeId === targetNode.nodeId && targetNode.moveType === 'child') ||
        (nodeId === targetNode.parentNodeId && targetNode.moveType !== 'child')
    ) {
        return `${color} ${color} ${color} ${color}`;
    }

    return TransparentBorderColors;
};

export type TreeDragDropProps<TData> = Readonly<{
    treeId: string;
    node: TreeNode<TData>;
    parentNodeId?: TreeNodeId;
    movable?: TreeMovableConfig<TData>;
    children?: ReactNode;
    isMovable: boolean;
}>;

export function TreeDragDrop<TData>({
    treeId,
    node,
    parentNodeId,
    movable,
    isMovable,
    children,
}: TreeDragDropProps<TData>) {
    const prevValidCheckRef = useRef<ValidCheck>();

    const {
        dragNode,
        targetNode,
        startMove,
        updateTarget,
        clearTarget,
        endMove,
        updatePreviewPosition,
    } = useTreeDragDrop(
        (state) => ({
            dragNode: state.dragNode,
            targetNode: state.targetNode,
            startMove: state.startMove,
            updateTarget: state.updateTarget,
            clearTarget: state.clearTarget,
            endMove: state.endMove,
            updatePreviewPosition: state.updatePreviewPosition,
        }),
        shallow
    );

    const isNodeBeingMoved =
        movable && dragNode && dragNode.treeId === treeId && dragNode.nodeId === node.id;

    const onDragStart = (event: React.DragEvent<HTMLElement>) => {
        if (!movable) {
            return;
        }
        startMove({ treeId, nodeId: node.id }, event.pageX, event.pageY);
        if (event.nativeEvent.target) {
            const dragIcon = document.createElement('img');
            dragIcon.src = BlankIcon;
            dragIcon.width = 0;
            dragIcon.height = 0;
            event.dataTransfer.setDragImage(dragIcon, -10, -10);
        }
        event.stopPropagation();
    };

    const onDrag = (event: React.DragEvent<HTMLElement>) => {
        if (isNodeBeingMoved) {
            updatePreviewPosition(event.pageX, event.pageY);
        }
    };

    const onDragEnd = (event: React.DragEvent<HTMLElement>) => {
        endMove();
        event.preventDefault();
        event.stopPropagation();
    };

    const onDragOver = (event: React.DragEvent<HTMLElement>) => {
        const { dataTransfer } = event.nativeEvent;

        const complete = (isValid: boolean) => {
            prevValidCheckRef.current = { x: event.pageX, y: event.pageY, isValid };
            if (dataTransfer) {
                dataTransfer.dropEffect = isValid ? 'move' : 'none';
            }
            event.preventDefault();
            event.stopPropagation();
        };

        if (!movable || !dragNode || dragNode.nodeId === node.id) {
            complete(false);
        } else if (
            event.pageX === prevValidCheckRef.current?.x &&
            event.pageY === prevValidCheckRef.current.y
        ) {
            complete(prevValidCheckRef.current.isValid);
        } else {
            const moveType = getTargetMoveType(event);

            const moveInfo = moveType
                ? {
                      treeId: dragNode.treeId,
                      nodeId: dragNode.nodeId,
                      moveType,
                      targetTreeId: treeId,
                      targetNodeId: node.id,
                  }
                : undefined;

            const isValid = moveInfo !== undefined && movable.canMoveTo(moveInfo);

            if (moveInfo) {
                updateTarget({
                    treeId,
                    nodeId: node.id,
                    moveType: moveInfo.moveType,
                    parentNodeId,
                    isValid,
                });
            } else {
                clearTarget();
            }

            complete(isValid);
        }
    };

    const onDrop = (event: React.DragEvent<HTMLElement>) => {
        if (movable && dragNode) {
            endMove();
            if (dragNode.nodeId !== node.id) {
                const moveType = getTargetMoveType(event);

                const moveInfo = moveType
                    ? {
                          treeId: dragNode.treeId,
                          nodeId: dragNode.nodeId,
                          moveType,
                          targetTreeId: treeId,
                          targetNodeId: node.id,
                      }
                    : undefined;

                const isValid = moveInfo !== undefined && movable.canMoveTo(moveInfo);

                if (moveInfo && isValid) {
                    movable.onMove(moveInfo);
                }
            }
        }
    };

    const borderColors = useMemo(
        () =>
            getBorderColors({
                treeId,
                nodeId: node.id,
                dragNode,
                targetNode,
                color: tokens.text.primary,
            }),
        [dragNode, node.id, targetNode, treeId]
    );

    return (
        <DragDropContainer
            draggable={isMovable}
            onDragStart={onDragStart}
            onDrag={onDrag}
            onDragEnd={onDragEnd}
            onDragEnter={onDragOver}
            onDragExit={clearTarget}
            onDragOver={onDragOver}
            onDrop={onDrop}
            style={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: borderColors,
            }}
        >
            <div style={{ opacity: isNodeBeingMoved ? MoveNodeOpacity : undefined }}>
                {children}
            </div>
            {movable && isNodeBeingMoved && (
                <DataTreeDragPreview<TData> node={node} movable={movable} />
            )}
        </DragDropContainer>
    );
}
